import { useRef, useEffect, useState } from "react";
import { Box, Flex, Text, Button } from "@chakra-ui/react";
import { colors } from "@/theme/colors";

export const SignaturePad = ({ value, onChange, testId }) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [hasInk, setHasInk] = useState(!!value);
  // Initial value only — the canvas is drawn once on mount; later strokes live on the canvas itself.
  const initialValue = useRef(value);

  useEffect(() => {
    const canvas = canvasRef.current;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#1F2937";
    if (initialValue.current) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, rect.width, rect.height);
      img.src = initialValue.current;
    }
  }, []);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    const ctx = canvasRef.current.getContext("2d", { willReadFrequently: true });
    const { x, y } = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const moveTo = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d", { willReadFrequently: true });
    const { x, y } = pos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    if (!hasInk) setHasInk(true);
  };

  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    onChange(canvasRef.current.toDataURL("image/png"));
  };

  const clear = () => {
    const canvas = canvasRef.current;
    canvas.getContext("2d", { willReadFrequently: true }).clearRect(0, 0, canvas.width, canvas.height);
    setHasInk(false);
    onChange(null);
  };

  return (
    <Box>
      <Box as="canvas" ref={canvasRef} data-testid={testId}
        w="full" h="150px" bg={colors.white} border="1.5px dashed" borderColor={colors.border}
        borderRadius="10px" touchAction="none" cursor="crosshair"
        onPointerDown={start} onPointerMove={moveTo} onPointerUp={end} onPointerLeave={end} />
      <Flex justify="space-between" align="center" mt="1.5">
        <Text fontSize="xs" color={colors.textSecondary}>{hasInk ? "Signed" : "Sign with your finger or mouse"}</Text>
        <Button size="xs" variant="ghost" onClick={clear} data-testid={`${testId}-clear`}>Clear</Button>
      </Flex>
    </Box>
  );
};
